import { Button } from "@/components/core/buttons/Button";
import { cn, modalWidthClass } from "@/lib/utils";
import { Text } from "components/core/Text";
import { Tooltip } from "components/core/Tooltip";
import { MixModal } from "components/core/modals/MixModal";
import {
    GetUserInterestTagsDocument,
    Mix_Content_Tag_Enum_Enum,
    useDeleteUserInterestTagsMutation,
    useGetUserInterestTagsQuery,
    useSessionQuery,
    useUpsertUserInterestTagsMutation
} from "graphql/generated";
import { useWindowSize } from "hooks/useWindowSize";
import Image from "next/image";
import { ComponentProps, useEffect, useMemo, useState } from "react";
import {
    MIX_CONTENT_TAG_ENUM_KOREAN,
    ORDERED_MIX_CONTENT_TAG_ENUM,
    SHINHAN_INVEST_TAG_LIST,
    TRIAL_TAG_LIST
} from "utils/enum";

import { Divider } from "../Divider";
import { Checkbox } from "../inputs/CheckBox";

export const SelectInterestTagModal = ({
    isOpen,
    onRequestClose
}: ComponentProps<typeof MixModal>) => {
    const { isMobile } = useWindowSize();
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [isCompleting, setIsCompleting] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [isFirstToggle, setIsFirstToggle] = useState(true);

    const { data: sessionData } = useSessionQuery();
    const userId = sessionData?.session?.me?.id;
    const userGroup = sessionData?.session?.me?.group_id;

    // Helper function to check if user has Shinhan access
    const hasShinhanAccess = useMemo(() => {
        const hasAccess = userGroup === "shinhaninvest";
        return hasAccess;
    }, [userGroup]);

    const defaultTags = useMemo(
        () =>
            ORDERED_MIX_CONTENT_TAG_ENUM.filter(
                (tag) =>
                    !(SHINHAN_INVEST_TAG_LIST as string[]).includes(tag) &&
                    !(TRIAL_TAG_LIST as string[]).includes(tag)
            ),
        []
    );

    const { data: userInterestTagsData, loading: isLoadingTags } =
        useGetUserInterestTagsQuery({
            variables: { userId: userId! },
            skip: !userId // userId가 없으면 query 실행하지 않음
        });

    const [upsertUserInterestTags, { loading: isUpsertLoading }] =
        useUpsertUserInterestTagsMutation({
            refetchQueries: [
                { query: GetUserInterestTagsDocument, variables: { userId } }
            ]
        });
    const [deleteUserInterestTags, { loading: isDeleteLoading }] =
        useDeleteUserInterestTagsMutation({
            refetchQueries: [
                { query: GetUserInterestTagsDocument, variables: { userId } }
            ]
        });

    // DB에서 조회한 사용자 관심 태그로 초기값 설정
    useEffect(() => {
        if (!isCompleting && userInterestTagsData?.mix_user_interest_tag) {
            const tagsFromDB = new Set(
                userInterestTagsData.mix_user_interest_tag.map(
                    (item) => item.tag_value as string
                )
            );

            if (tagsFromDB.size > 0) {
                // DB에 데이터가 있으면 해당 태그들을 선택
                setSelectedTags(tagsFromDB);
                setIsFirstToggle(false);
            } else {
                // DB에 데이터가 없으면 모든 태그를 선택된 상태로 초기화
                const allTags = new Set([
                    ...defaultTags,
                    ...(hasShinhanAccess ? SHINHAN_INVEST_TAG_LIST : [])
                ]);
                setSelectedTags(allTags);
                setIsFirstToggle(true);
            }
        } else if (!isCompleting && userInterestTagsData && !isLoadingTags) {
            // 쿼리는 완료되었지만 데이터가 없는 경우 (첫 방문)
            const allTags = new Set([
                ...defaultTags,
                ...(hasShinhanAccess ? SHINHAN_INVEST_TAG_LIST : [])
            ]);
            setSelectedTags(allTags);
            setIsFirstToggle(true);
        }
    }, [
        userInterestTagsData,
        isCompleting,
        isLoadingTags,
        defaultTags,
        hasShinhanAccess
    ]);

    const toggleTag = async (tag: string) => {
        if (isFirstToggle && userId) {
            // 첫 토글 시: 현재 선택된 태그들(OFF한 태그 제외)만 upsert
            setIsFirstToggle(false);

            // 로컬 상태 먼저 업데이트 (OFF한 태그 제거)
            const updatedTags = new Set(selectedTags);
            updatedTags.delete(tag);
            setSelectedTags(updatedTags);

            try {
                // 업데이트된 태그들만 upsert
                if (updatedTags.size > 0) {
                    const tagObjects = Array.from(updatedTags).map(
                        (tagValue, index) => ({
                            user_id: userId,
                            tag_value: tagValue as Mix_Content_Tag_Enum_Enum,
                            priority: index + 1
                        })
                    );

                    await upsertUserInterestTags({
                        variables: {
                            objects: tagObjects
                        }
                    });
                }
            } catch (error) {
                setIsFirstToggle(true); // 실패 시 되돌리기
                // 실패 시 로컬 상태도 되돌리기
                setSelectedTags(selectedTags);
            }
        } else {
            // 일반 토글 로직
            setSelectedTags((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(tag)) {
                    newSet.delete(tag);
                } else {
                    newSet.add(tag);
                }
                return newSet;
            });
        }
    };

    const toggleAllTags = (tagList: readonly string[]) => {
        const allSelected = tagList.every((tag) => selectedTags.has(tag));

        if (allSelected) {
            // 전체 해제: 선택된 태그들을 하나씩 해제
            tagList.forEach((tag) => {
                if (selectedTags.has(tag)) {
                    toggleTag(tag);
                }
            });
        } else {
            // 전체 선택: 선택되지 않은 태그들을 하나씩 선택
            tagList.forEach((tag) => {
                if (!selectedTags.has(tag)) {
                    toggleTag(tag);
                }
            });
        }
    };

    const isAllTagsSelected = (tagList: readonly string[]) => {
        return (
            tagList.length > 0 && tagList.every((tag) => selectedTags.has(tag))
        );
    };

    const isNoTagsSelected = selectedTags.size === 0 && !isFirstToggle;

    // 태그가 선택되면 tooltip 숨기기
    useEffect(() => {
        if (!isNoTagsSelected) {
            setShowTooltip(false);
        } else {
            setShowTooltip(true);
            setTimeout(() => setShowTooltip(false), 3000);
        }
    }, [isNoTagsSelected]);

    const handleRequestClose = () => {
        if (isNoTagsSelected) {
            setShowTooltip(true);
            setTimeout(() => setShowTooltip(false), 3000);
            return;
        }
        onRequestClose?.();
    };

    const handleComplete = async () => {
        if (!userId) {
            return;
        }

        setIsCompleting(true);

        const selectedTagsArray = Array.from(selectedTags);

        try {
            // 1. 먼저 선택되지 않은 태그들을 삭제
            await deleteUserInterestTags({
                variables: {
                    userId,
                    tagValues: selectedTagsArray as Mix_Content_Tag_Enum_Enum[]
                }
            });

            // 2. 선택된 태그들을 priority와 함께 upsert
            if (selectedTagsArray.length > 0) {
                const tagObjects = selectedTagsArray.map((tag, index) => ({
                    user_id: userId,
                    tag_value: tag as Mix_Content_Tag_Enum_Enum,
                    priority: index + 1 // 1부터 시작하는 priority
                }));

                await upsertUserInterestTags({
                    variables: {
                        objects: tagObjects
                    }
                });
            }

            setIsCompleting(false);
            handleRequestClose();
        } catch (error) {
            // TODO: 에러 토스트 메시지 표시
            setIsCompleting(false);
        }
    };

    const renderTagButtons = (tags: readonly string[]) => {
        return tags.map((tag) => (
            <Button
                key={tag}
                variant="filled"
                color={selectedTags.has(tag) ? "primary" : "grey"}
                size="medium"
                onClick={() => toggleTag(tag)}
                className={cn(
                    `h-[27px] rounded-xl px-3.5 py-[14px] pb-[14px] text-xs
                    font-semibold leading-[139.9999976158142%] transition-all
                    duration-200`,
                    "hover:border-primary-main",
                    !selectedTags.has(tag) && "hover:bg-primary-hover",
                    "md:px-[10px] md:py-4"
                )}
            >
                {MIX_CONTENT_TAG_ENUM_KOREAN[tag]}
            </Button>
        ));
    };

    return (
        <MixModal isOpen={isOpen} onRequestClose={handleRequestClose}>
            <div
                className={cn(
                    `relative mx-6 flex flex-col items-center justify-center
                    rounded-[24px] bg-common-white-main px-10 py-12
                    shadow-[0px_8px_32px_0px_rgba(0,0,0,0.16)] dark:bg-grey-50
                    md:rounded-[20px] md:px-6 md:py-8`,
                    modalWidthClass
                )}
            >
                {/* 제목 */}
                <div className="mb-8 text-center">
                    <Text variant={isMobile ? "h3" : "h2"}>
                        관심 주제를 선택해 주세요.
                    </Text>
                    <br />
                    <Text
                        variant={isMobile ? "body3" : "body1"}
                        color="text.secondary"
                        className="mt-3"
                    >
                        이후에도 언제든 변경할 수 있어요.
                    </Text>
                </div>

                {/* 신한투자증권 전용 태그 (조건부 렌더링) */}
                {hasShinhanAccess && (
                    <>
                        <div
                            className="mb-4 ml-5 flex items-center gap-2
                                self-start"
                        >
                            <Image
                                src="/img/logo/shinhaninvest.png"
                                alt="shinhaninvest"
                                width={24}
                                height={24}
                            />
                            <Text variant="button.small" color="text.primary">
                                신한투자증권 전용 카테고리
                            </Text>
                        </div>
                        <div
                            className="mb-2 ml-3 flex cursor-pointer flex-wrap
                                items-center justify-start gap-0.5 self-start"
                            onClick={() =>
                                toggleAllTags(SHINHAN_INVEST_TAG_LIST)
                            }
                        >
                            <Checkbox
                                size="medium"
                                checked={isAllTagsSelected(
                                    SHINHAN_INVEST_TAG_LIST
                                )}
                            />
                            <span className="text-primary-main">
                                모든 카테고리
                            </span>
                        </div>
                        {/* 그라데이션 페이드로 스크롤 가능 여부를 시각적으로 표시 */}
                        <div className="relative mb-10 w-full max-w-[483px]">
                            <div className="flex flex-wrap justify-start gap-3 max-h-[130px] overflow-y-auto pb-6">
                                {renderTagButtons(SHINHAN_INVEST_TAG_LIST)}
                            </div>
                            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-common-white-main to-transparent dark:from-grey-50" />
                        </div>
                        <Divider className="mb-10" />
                    </>
                )}

                {/* 기본 태그 (신한투자증권 일 경우 '믹스 기본 카테고리'임을 표시)*/}
                {hasShinhanAccess && (
                    <div
                        className="mb-4 ml-5 flex items-center gap-2 self-start"
                    >
                        <Image
                            src="/img/logo/mix.png"
                            alt="shinhaninvest"
                            width={24}
                            height={24}
                        />
                        <Text variant="button.small" color="text.primary">
                            믹스 기본 카테고리
                        </Text>
                    </div>
                )}
                <div
                    className="mb-2 flex cursor-pointer flex-wrap items-center
                        justify-start gap-0.5 self-start md:ml-9"
                    onClick={() => toggleAllTags(defaultTags)}
                >
                    <Checkbox
                        size="medium"
                        checked={isAllTagsSelected(defaultTags)}
                    />
                    <span className="text-primary-main">모든 카테고리</span>
                </div>
                {/* 그라데이션 페이드로 스크롤 가능 여부를 시각적으로 표시 */}
                <div className="relative mb-10 w-full max-w-[483px]">
                    <div className="flex flex-wrap justify-start gap-3 max-h-[130px] overflow-y-auto pb-6">
                        {renderTagButtons(defaultTags)}
                    </div>
                    <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-common-white-main to-transparent dark:from-grey-50" />
                </div>

                {/* 하단 버튼 */}
                <div className="flex w-full items-center justify-end">
                    <Tooltip
                        placement="top"
                        trigger="mouseover"
                        open={showTooltip || undefined}
                    >
                        <Tooltip.Trigger asChild={false}>
                            <Button
                                variant="filled"
                                color="secondary"
                                size="small"
                                onClick={handleComplete}
                                disabled={
                                    isUpsertLoading ||
                                    isDeleteLoading ||
                                    isLoadingTags ||
                                    isCompleting ||
                                    isNoTagsSelected
                                }
                                className="px-4 py-1.5"
                            >
                                {isLoadingTags
                                    ? "로딩 중..."
                                    : isDeleteLoading ||
                                        isUpsertLoading ||
                                        isCompleting
                                      ? "저장 중..."
                                      : "완료"}
                            </Button>
                        </Tooltip.Trigger>
                        {isNoTagsSelected && (
                            <Tooltip.Content
                                className="z-[1000] max-w-[260px] break-keep
                                    rounded-md px-2.5 py-2 text-xs"
                            >
                                카테고리를 최소 1개 이상 선택해 주세요
                            </Tooltip.Content>
                        )}
                    </Tooltip>
                </div>
            </div>
        </MixModal>
    );
};
